import * as React from "react";
import {Button, Table} from "antd";
import {ColumnProps} from "antd/lib/table";
import {simpleColumn} from "../utils/table-utils";
import locales from "../locales";
import {renderFixedPriceColumn, renderPriceColumn} from "../utils/number-utils";
import {AmountType, getAmountUnitForAmountType} from "../common/AmountType";
import {VAT, vatTypeToText} from "../common/vat";
import InvoiceEntryEditModal from './InvoiceEntryEditModal';
import ButtonGroup from 'antd/lib/button/button-group';
import {
  getAmountFromOverrideOrItem,
  getAmountTypeFromOverrideOrItem,
  getPriceFromOverrideOrItem,
  getTotalFromOverrideOrItem,
  InvoiceEntry
} from "../api/dtos/summaries";
import ButtonBar from "../common/ButtonBar";


interface Props {
  entries: InvoiceEntry[];
  editable: boolean;
  onChange: (entries: InvoiceEntry[]) => void;
}

interface State {
  entries: InvoiceEntry[],
  editedInvoiceEntry?: InvoiceEntry;
  editedInvoiceEntryIdx?: number;
}

class InvoicingSummaryTable extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    const entries = this.normalizeEntries(props.entries);
    this.state = {entries, editedInvoiceEntry: undefined, editedInvoiceEntryIdx: undefined};
    props.onChange(entries);
  }

  public render(): React.ReactElement {
    const {entries, editedInvoiceEntry} = this.state;
    const columns: Array<ColumnProps<InvoiceEntry>> = [
      simpleColumn(locales.entry_name, i => i.name, 160),
      simpleColumn(locales.amount, this.renderAmountColumn, 90),
      simpleColumn(locales.amount_type, this.renderAmountTypeColumn, 100),
      {
        ...simpleColumn(locales.net_unit_price, this.renderUnitPriceColumn),
        className: 'align-right',
      },
      simpleColumn(locales.vat, this.renderVatColumn, 106),
      {
        ...simpleColumn(locales.net_price, this.renderTotalColumn),
        className: 'align-right',
      },
      {
        ...simpleColumn(locales.gross_price, this.renderGrossTotalColumn),
        className: 'align-right',
      },
      simpleColumn(locales.entry_comment, this.renderCommentColumn),
      {
        key: 'actions',
        render: this.renderActionColumn,
        width: 88,
      },
    ];

    return (
      <>
        <InvoiceEntryEditModal
          invoiceEntry={editedInvoiceEntry}
          onSave={this.onEntryChanged}
          onCancel={this.onEditEntryCancel}
        />

        <ButtonBar style={{marginBottom: 8}}>
          <Button size="small" type="primary" icon="plus" disabled={!this.props.editable}
                  onClick={this.onAddEntryClick}>{locales.add}</Button>
        </ButtonBar>

        <Table
          dataSource={entries}
          columns={columns}
          rowKey={this.rowKeyFn}
          bordered={true}
          size="small"
          pagination={false}
          scroll={{x: 1024}}
        />
      </>
    );
  }

  private renderAmountTypeColumn = (entry: InvoiceEntry): React.ReactNode => {
    const amountType = getAmountTypeFromOverrideOrItem(entry);
    return getAmountUnitForAmountType(amountType) ?? entry.amountType ?? '';
  };

  private renderAmountColumn = (entry: InvoiceEntry): React.ReactNode => {
    const amount = getAmountFromOverrideOrItem(entry);

    return amount ?? '-';
  };

  private renderUnitPriceColumn = (entry: InvoiceEntry): React.ReactNode => {
    const price = getPriceFromOverrideOrItem(entry);
    return renderPriceColumn(price);
  };

  private renderVatColumn = (entry: InvoiceEntry): React.ReactNode => {
    return entry.vat ? vatTypeToText[entry.vat] : '';
  };

  private renderTotalColumn = (entry: InvoiceEntry): React.ReactNode => {
    const total = getTotalFromOverrideOrItem(entry);
    return <b>{renderFixedPriceColumn(total)}</b>
  };

  private renderGrossTotalColumn = (entry: InvoiceEntry): React.ReactNode => {
    return <b>{renderFixedPriceColumn(entry.grossPrice)}</b>
  };

  private renderCommentColumn = (entry: InvoiceEntry): React.ReactNode => {
    return <div>{entry.comment}</div>;
  };

  private renderActionColumn = (_: any, entry: InvoiceEntry, index: number): React.ReactNode => {
    const {editable} = this.props;
    const onEditClick = () => this.onEditEntryClick(entry, index);
    const onDeleteClick = () => this.onDeleteEntryClick(index);
    return (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button icon="edit" onClick={onEditClick} title={locales.edit} disabled={!editable}/>
          <Button icon="delete" onClick={onDeleteClick} title={locales.delete} disabled={!editable}/>
        </ButtonGroup>
      </div>
    );
  };

  private onEditEntryClick = (entry: InvoiceEntry, index: number) => {
    this.setState({
      editedInvoiceEntry: entry,
      editedInvoiceEntryIdx: index,
    })
  };

  private onEntryChanged = (entry: InvoiceEntry) => {
    const {editedInvoiceEntryIdx, entries} = this.state;
    const newEntry = this.recalculateEntry(entry);
    const newEntries = [...entries];
    if (editedInvoiceEntryIdx === undefined) {
      newEntries.push(newEntry);
    } else {
      newEntries.splice(editedInvoiceEntryIdx, 1, newEntry);
    }
    this.setState({
      entries: newEntries,
      editedInvoiceEntry: undefined,
      editedInvoiceEntryIdx: undefined,
    });
    this.props.onChange(newEntries);
  };

  private onEditEntryCancel = () => {
    this.setState({
      editedInvoiceEntry: undefined,
      editedInvoiceEntryIdx: undefined,
    })
  };

  private onDeleteEntryClick = (index: number) => {
    const entries = [...this.state.entries];
    entries.splice(index, 1);
    this.setState({entries});
    this.props.onChange(entries);
  };

  private normalizeEntries = (entries: InvoiceEntry[]): InvoiceEntry[] => {
    return entries.map(entry => {
      const vat = entry.vat || VAT.P27;
      const netPrice = entry.price! * entry.amount!
      const grossMultiplier = vat === VAT.P27 ? 1.27 : 1;
      const vatPrice = Number(((grossMultiplier - 1) * netPrice));
      return {
        ...entry,
        vat,
        total: Number(netPrice.toFixed()),
        grossPrice: entry.grossPrice ?? (netPrice + vatPrice),
      };
    });
  };

  private recalculateEntry = (entry: InvoiceEntry): InvoiceEntry => {
    const amount = entry.amount;
    const total = amount === null ? null : amount * entry.price!;
    const grossMultiplier = entry.vat === VAT.P27 ? 1.27 : 1;
    const grossPrice = total === null ? undefined : Number((total * grossMultiplier).toFixed());
    return {
      ...entry,
      total,
      grossPrice,
      override: entry.override ? {
        ...entry.override,
        price: entry.price || 0,
        amountType: entry.amountType,
        amount: amount || 0,
        total,
      } : undefined,
    };
  };

  private rowKeyFn = (entry: InvoiceEntry, index: number) => `entry-${entry.id || 'new'}-${index}`;

  private onAddEntryClick = () => {
    this.setState({
      editedInvoiceEntry: {
        name: '',
        amount: 1,
        amountType: AmountType.PCS,
        price: null,
        total: 0,
        vat: VAT.P27,
        grossPrice: 0,
        comment: null,
      },
      editedInvoiceEntryIdx: undefined,
    });
  }
}

export default InvoicingSummaryTable;



// WEBPACK FOOTER //
// ./src/invoicing/InvoicingSummaryTable.tsx